import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { QueryParams } from '@aid/shared/types/interfaces/query-params.interface';
import {
  expand,
  filter,
  first,
  map,
  reduce,
  switchMap,
  tap
} from 'rxjs/operators';
import { PaginationResponse } from '@aid/shared/types/interfaces';
import { HttpClient } from '@angular/common/http';
import { defined } from '@aid/shared/operators/defined';

export abstract class PaginationService<T> {
  protected values = new BehaviorSubject<T[] | null>([]);
  protected queryParams = new BehaviorSubject<QueryParams>(
    this.getInitialQueryParams()
  );
  protected loading = new BehaviorSubject<boolean | null>(null);
  protected loadingMoreData = new BehaviorSubject<boolean | null>(null);

  protected count = new BehaviorSubject<number | null>(null);
  protected hasNext = new BehaviorSubject<boolean | null>(null);
  protected cursor = new BehaviorSubject<string | null>(null);

  protected constructor(protected _http: HttpClient, protected cache = false) {}

  abstract get url(): string;

  protected getInitialQueryParams() {
    return new QueryParams();
  }

  refresh() {
    this.values = new BehaviorSubject<T[] | null>(null);
    this.queryParams = new BehaviorSubject<QueryParams>(
      this.getInitialQueryParams()
    );
    this.loading = new BehaviorSubject<boolean | null>(null);
    this.count = new BehaviorSubject<number | null>(null);
  }

  search(search: string) {
    const queryParams = {
      ...this.queryParams.value,
      ...new QueryParams()
    };

    if (search) {
      queryParams.search = search;
    } else {
      delete queryParams.search;
    }

    this.queryParams.next(queryParams);
  }

  loadNextPage() {
    if (!this.hasNext.value) {
      return;
    }
    if (this.loading.value) {
      return;
    }
    const queryParams: QueryParams = {
      ...this.queryParamsValue
    };

    if (this.cursor.value) {
      queryParams.cursor = this.cursor.value;
      delete queryParams.page;
    } else {
      delete queryParams.cursor;
      queryParams.page = (this.queryParamsValue.page ?? 0) + 1;
    }

    this.loadingMoreData.next(true);
    this.queryParams.next(queryParams);
  }

  changePage(pageNumber: number, pageSize: number) {
    const queryParams = new QueryParams();
    queryParams.page = pageNumber;
    queryParams.pageSize = pageSize;
    this.queryParams.next({ ...this.queryParams.value, ...queryParams });
  }

  changeParams(queryParams: QueryParams) {
    this.queryParams.next({ ...queryParams });
  }

  subscribeValues$(): Observable<T[]> {
    return this.queryParams.pipe(
      filter(value => !!value && this.filterSubscribe()),
      tap(() => this.loading.next(true)),
      switchMap(params => this.list(params)),
      tap((response: PaginationResponse<T>) => this.count.next(response.count)),
      tap((response: PaginationResponse<T>) =>
        this.hasNext.next(!!response.next)
      ),
      tap((response: PaginationResponse<T>) => this.setCursor(response)),
      map((response: PaginationResponse<T>) => response.results),
      switchMap(results => {
        this.setValues(results);
        this.loadingMoreData.next(false);
        return this.values.pipe(defined());
      }),
      tap(() => this.loading.next(false))
    );
  }

  list(queryParams: QueryParams): Observable<PaginationResponse<T>> {
    return this.listAs<T>(queryParams);
  }

  // The same request for responses shaped differently from T, such as
  // embedded relations. Kept apart from list() so subclasses can still
  // override list() with their concrete type.
  listAs<R>(queryParams: QueryParams): Observable<PaginationResponse<R>> {
    const params = this.getParams(queryParams);
    return this._http.get<PaginationResponse<R>>(`${this.url}`, { params });
  }

  get loading$() {
    return this.loading.asObservable();
  }

  get loadingMoreData$() {
    return this.loadingMoreData.asObservable();
  }

  get count$() {
    return this.count.asObservable();
  }

  get hasNext$() {
    return this.hasNext.asObservable();
  }

  get queryParams$() {
    return this.queryParams.asObservable();
  }

  get queryParamsValue() {
    return this.queryParams.getValue();
  }

  get pageSize$(): Observable<number | undefined> {
    return this.queryParams$.pipe(map(params => params.pageSize));
  }

  get pageIndex$(): Observable<number | undefined> {
    return this.queryParams$.pipe(map(params => params.page));
  }

  getParams(queryParams: QueryParams) {
    const pageSize = queryParams?.pageSize?.toString() ?? '20';
    const params: any = {
      ...queryParams,
      pageSize
    };
    if (queryParams.page) {
      params.page = queryParams.page.toString();
    } else {
      delete params.page;
    }
    if (queryParams.cursor) {
      params.cursor = queryParams.cursor;
    }
    return params;
  }

  /**
   * Method used for subclasses to stop the observable chain
   * For example if the route is a nested one and we need to wait for a
   * specific resource
   */
  protected filterSubscribe(): boolean {
    return true;
  }

  protected setValues(values: T[]) {
    if (!(this.cache && this.loadingMoreData.getValue())) {
      this.values.next(values);
    } else {
      this.values.next([...(this.values.value ?? []), ...values]);
    }
  }

  private setCursor(response: PaginationResponse<T>) {
    const cursor =
      typeof response.next === 'string'
        ? this.getCursorFromURL(response.next)
        : null;
    this.cursor.next(cursor);
  }

  private getCursorFromURL(url: string) {
    const name = 'cursor';
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(url);
    return results === null
      ? null
      : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }
}
